"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import Turnstile from "../Turnstile";

export default function HardCodedContactForm() {
    const t = useTranslations("ContactPage.form");

    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        name: "",
        message: "",
        processing: false,
        subscription: false,
    });

    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [captchaToken, setCaptchaToken] = useState("");
    const [captchaResetKey, setCaptchaResetKey] = useState(0);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!captchaToken || status === "loading") return;
        setStatus("loading");

        try {
            const res = await fetch("/api/hubspot", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...formData, captchaToken }),
            });

            if (!res.ok) throw new Error("Fehler beim Senden");

            setStatus("success");
            setFormData({
                firstname: "",
                lastname: "",
                email: "",
                phone: "",
                name: "",
                message: "",
                subscription: false,
                processing: false,
            });
        } catch (error) {
            setStatus("error");
        } finally {
            setCaptchaToken("");
            setCaptchaResetKey((key) => key + 1);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6 contact-form">
            <label>
                <span className="d-body">{t("name")}</span>
                <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border-b"
                />
            </label>

            <label>
                <span className="d-body">{t("lastname")}</span>
                <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    required
                    className="w-full p-2 border-b"
                />
            </label>

            <label>
                <span className="d-body">{t("email")}</span>
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    className="w-full p-2 border-b"
                />
            </label>

            <label>
                <span className="d-body">{t("phone")}</span>
                <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-2 border-b"
                />
            </label>

            <label>
                <span className="d-body">{t("company")}</span>
                <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full p-2 border-b"
                />
            </label>

            <label>
                <span className="d-body">{t("projectDescription")}</span>
                <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full p-2 border-b"
                />
            </label>

            <p className="d-legal text-black/70">{t("dataUsage")}</p>

            <div className="flex flex-col gap-4">
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        name="subscription"
                        onChange={() => {
                            setFormData((prev) => ({ ...prev, subscription: !prev.subscription }));
                        }}
                        checked={formData.subscription}
                        required
                        className="w-4 h-4 border min-w-4 min-h-4"
                    />
                    <span className="d-body">{t("contacting")}</span>
                </label>

                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        onChange={() => {
                            setFormData((prev) => ({ ...prev, processing: !prev.processing }));
                        }}
                        checked={formData.processing}
                        name="processing"
                        required
                        className="w-4 h-4 border min-w-4 min-h-4"
                    />
                    <span className="d-body">{t("processing")}</span>
                </label>
            </div>
            <Turnstile onToken={setCaptchaToken} resetKey={captchaResetKey} />
            <div>
                <button
                    type="submit"
                    disabled={status === "loading" || !captchaToken}
                    className="relative flex items-center gap-2 px-6 py-3 border border-black rounded-full cursor-pointer m-button-small md:d-button-small btn-inverted"
                >
                    {status === "loading" ? t("sending") : t("submit")}

                    <svg
                        className="w-6 h-6"
                        width="36"
                        height="37"
                        viewBox="0 0 36 37"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <g>
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M13.5006 6.875H29.6256V21.7812H27.3756V10.716L8.29607 29.7955L6.70508 28.2045L25.7846 9.125H13.5006V6.875Z"
                                fill="black"
                            />
                        </g>
                    </svg>
                </button>
            </div>
            {status === "success" && <p>{t("successMessage")}</p>}
            {status === "error" && <p className="text-red-600">{t("errorMessage")}</p>}
        </form>
    );
}
